import React, { memo, useState } from 'react';
import {
  View,
  TextInput,
  Text,
  ActivityIndicator,
  UIManager,
  Platform,
} from 'react-native';
import classes from '../../styles/globalClasses';
import gs from '../../styles/globalStyles';
import ls from './style';
import FadeInAnimation from '../FadinAnimation/FadinAnimation';

import { useOTPVerification } from './useOTP';
import { useTranslation } from 'react-i18next';
import ThemedTouchableOpacity from '../../theme/Components/ThemedTouchableOpacity'
import { Theme, ThemedView, themeColorSet } from '@app/theme';
import Button from '@app/sharedComponents/Button/Button';
import { ThemedText } from '@app/theme/Components';
import SystemWarning from '../../../assets/Icons/SystemWarning';
import { useTheme } from '@shopify/restyle';


// Define the props for the OTPVerification component
interface OTPVerificationProp {
  noOfDigits?: number; // Number of OTP digits, default is 6
  hasError: string; // Error message to display
  resendTimerCount: number; // Timer count for resending OTP
  handleOtpInput: (otp: string[]) => void; // Function to handle OTP input
  triggerResendOTP: () => void; // Function to trigger resending OTP
  verifying: boolean; // Boolean to indicate if OTP is being verified
  blockResend: boolean;
  isDarkMode?: boolean;
  onCustomerFacingIssue?:() => void
}

// Enable layout animation for Android
if (UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

// OTPVerification component definition
const OTPVerification: React.FC<OTPVerificationProp> = ({
  noOfDigits = 6, // Default number of OTP digits is 6
  hasError,
  resendTimerCount,
  handleOtpInput,
  triggerResendOTP,
  verifying,
  blockResend,
  isDarkMode,
  onCustomerFacingIssue
}) => {
  const showErrorBorder = !verifying && hasError !== '';
  const [cursorPosition, setCursorPosition] = useState(1);
  const [resendCount, setResendCount] = useState(0);
  const { t } = useTranslation('translation', {
    keyPrefix: 'OTP_INPUT',
  });

  const {colors} = useTheme<Theme>()

  const {
    otp,
    timer,
    canResend,
    showInfo,
    showHint,
    otpResent,
    focusedIndex,
    inputRefs,
    handleFocus,
    handleOtpChange,
    handleBackspace,
    handleResend,
    
  } = useOTPVerification({
    noOfDigits,
    resendTimerCount,
    handleOtpInput,
    triggerResendOTP,
    blockResend,
    showErrorBorder,
    cursorPosition
  });

  return (
    <>
      <View style={[ls.otpContainer, gs.setMarginLeft(-5)]}>
        {otp.map((digit, index) => {
          return (
            <TextInput
              editable={!blockResend}
              maxLength={index === noOfDigits - 1 ? 1 : undefined}
              textContentType='oneTimeCode'
              key={index}
              ref={ref => (inputRefs.current[index] = ref)}
              style={[
                ls.otpInput,
                { borderColor: index === focusedIndex ? themeColorSet['text-action-primary']: themeColorSet['border-primary'] },
                { color: colors['text-primary'] },
              ]}
              keyboardType="numeric"
              value={digit}
              onChangeText={text => {
                if(!verifying) {
                  handleOtpChange(text, index)
                }
              }}
              onKeyPress={event => handleBackspace(index, event)}
              textAlign="center"
              onFocus={(e) => {
                handleFocus(index)
              }}
              onSelectionChange={(e) => {
                setCursorPosition(e?.nativeEvent.selection.start >= 1 ? 1 : 0)
              }}
            />
          );
        })}
      </View>
      <View style={[gs.setHeight(20), gs.setMarginTop(6) , {marginBottom:12}]}>
        {verifying && (
          <FadeInAnimation delay={100} duration={1000}>
            <View style={[gs.flexRow]}>
              <ActivityIndicator color={colors['text-action-primary']} size={'small'} />
              <Text style={[classes.b1r, gs.setColor(colors['text-action-primary'])]}>
                {' '}
                {t('verifyingOtp')}
              </Text>
            </View>
          </FadeInAnimation>
        )}
        {!verifying && hasError && (
          <FadeInAnimation delay={100} duration={1000}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', flexShrink: 1, maxWidth: '100%' }}>
              <Text
                style={[
                  classes.b1r,
                  gs.setColor('rgb(240, 68, 56)'),
                ]}
              >
                {hasError}
              </Text>
            </View>
          </FadeInAnimation>
        )}
      </View>

      <View style={[gs.flexRow, gs.setMarginTop(16), gs.setMarginBottom(10)]}>
        <ThemedText variant='b3r' color='text-secondary'>{t('didntReceive') + '  '}</ThemedText>
    
        
        <ThemedTouchableOpacity eventName={(canResend && !blockResend) ? 'RESEND_OTP_BUTTON' : undefined} activeOpacity={(canResend && !blockResend) ? 0 : 1} style={[gs.flexRow]} onPress={()=>{
          
        if (canResend && !blockResend){
          setResendCount(resendCount + 1)
        }
        
          handleResend()
        }
        }>
          <ThemedText color='text-action-primary' variant='b3b'>{t('resendCta')}</ThemedText>

        </ThemedTouchableOpacity>
      </View>
      <View style={{  marginBottom : 10 }}>
        {showHint && otpResent && !showInfo && resendTimerCount - timer < 6 &&  resendCount <2 && (
          <FadeInAnimation delay={100} duration={1000}>
            <ThemedText color='text-success' variant='b3r'>{t('otpResent')}</ThemedText>
          </FadeInAnimation>
        )}
        {showInfo && (
          <FadeInAnimation delay={100} duration={1000}>
            <ThemedText color='text-primary' variant='b3r'>{t('timeRemaining' , {timer : timer})}</ThemedText>
          </FadeInAnimation>
        )}
        {
         resendCount>=2 &&(    
          
            <ThemedView padding='sm' backgroundColor='bg-fill-warning'  flexDirection='row' alignItems='center'  style={{marginBottom: 12, borderRadius: 8}}>
           <SystemWarning size="16"/>
           <ThemedText variant='b3r' style={{marginLeft: 3}}>
           Not receiving OTP?
           </ThemedText>
       
            <Button type='SOLID' overRideStyle={{width : 50, height : 30,marginLeft : 16, borderRadius : 4}} label='Yes' handlePress={onCustomerFacingIssue}/>
            <Button type='OUTLINED' overRideStyle={{width : 50, height : 30,marginLeft : 8,borderRadius : 4}} label='No' handlePress={()=>{setResendCount(1)}}/>
            </ThemedView>
          )
        }
        {Platform.OS === 'android' ? (
          <ThemedView height={10} />
        ) : null}
      </View>
    </>
  );
};

export default memo(OTPVerification);
